import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/main/MainLayout';
import FileCard from '../../components/FileCard/FileCard';
import FileAddButton from '../../components/FileAddButton/FileAddButton';
import FileCardInput from '../../components/FileCardInput/FileCardInput';
import useFileStore from '../../store/fileStore';
import useAuthStore from '../../store/authStore';
import * as S from './Upload.style';

const Upload = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    files,
    pagination,
    loading,
    error,
    filters,
    loadFiles,
    changePage,
    changeSort,
    changeFilter,
    searchFiles,
    addFile,
    updateFile,
    removeFile,
    clearError
  } = useFileStore();

  const [isAddingFile, setIsAddingFile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('uploaded_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('active');

  useEffect(() => {
    // 컴포넌트 마운트시 파일 목록 로드
    loadFiles();
    
    // 컴포넌트 언마운트시 에러 클리어
    return () => {
      clearError();
    };
  }, [loadFiles, clearError]);

  const handleDataMapping = (fileId) => {
    console.log('[UPLOAD]', 'Data mapping clicked for file:', fileId);
    const targetFile = files.find(file => file.file_id === fileId);
    
    if (targetFile) {
      navigate('/mapping', { 
        state: { 
          fileId,
          fileName: targetFile.filename,
          originalFileName: targetFile.filename
        } 
      });
    }
  };

  const handleMappingReset = async (fileId) => {
    console.log('[UPLOAD]', 'Mapping reset requested for file:', fileId);
    // FileCard 컴포넌트에서 처리됨
  };

  const handleEditFile = (fileId, fileData) => {
    console.log('[UPLOAD]', 'Edit file clicked for file:', fileId, fileData);
    
    navigate('/trim', { 
      state: { 
        fileId,
        fileName: fileData.filename,
        originalFileName: fileData.filename,
        fileSize: fileData.file_size,
        fileType: fileData.file_type
      } 
    });
  };

  const handleFileUpdate = (updatedFile, action) => {
    console.log('[UPLOAD]', 'File update:', action, updatedFile);
    
    switch (action) {
      case 'copy':
        // 새 파일 추가 (복사본)
        addFile(updatedFile);
        break;
        
      case 'reupload':
      case 'rename':
        // 기존 파일 정보 업데이트
        updateFile(updatedFile.id, {
          filename: updatedFile.fileName || updatedFile.filename,
          file_size: updatedFile.file_size,
          file_type: updatedFile.file_type,
          description: updatedFile.fileDescription || updatedFile.description
        });
        break;
        
      default:
        console.warn('[UPLOAD]', 'Unknown file update action:', action);
    }
  };

  const handleFileDelete = (fileId) => {
    console.log('[UPLOAD]', 'File delete completed:', fileId);
    removeFile(fileId);
  };

  const handleAddFileClick = () => {
    console.log('[UPLOAD]', 'Add file clicked');
    setIsAddingFile(true);
  };

  const handleFileUpload = (uploadedFile) => {
    console.log('[UPLOAD]', 'File upload completed:', uploadedFile);
    
    // 새로 업로드된 파일을 스토어에 추가
    addFile(uploadedFile);
    setIsAddingFile(false);
  };

  const handleCancelUpload = () => {
    console.log('[UPLOAD]', 'Upload cancelled');
    setIsAddingFile(false);
  };

  const handleRefresh = () => {
    console.log('[UPLOAD]', 'Refreshing file list');
    loadFiles();
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // 디바운싱을 위한 타이머
    if (handleSearch.timer) {
      clearTimeout(handleSearch.timer);
    }
    
    handleSearch.timer = setTimeout(() => {
      console.log('[UPLOAD]', 'Searching files:', value);
      searchFiles(value);
    }, 500);
  };

  const handleSortChange = (newSortBy) => {
    let newSortOrder = 'desc';
    
    // 같은 컬럼을 다시 클릭하면 정렬 순서 토글
    if (sortBy === newSortBy) {
      newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    }
    
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    
    console.log('[UPLOAD]', 'Changing sort:', newSortBy, newSortOrder);
    changeSort(newSortBy, newSortOrder);
  };

  const handleFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    console.log('[UPLOAD]', 'Changing filter:', newStatus);
    changeFilter(newStatus);
  };

  const handlePageChange = (page) => {
    console.log('[UPLOAD]', 'Changing page:', page);
    changePage(page);
  };

  // 백엔드 응답 데이터를 화면 표시용으로 변환
  const formatFileForDisplay = (file) => ({
    id: file.file_id,
    name: file.filename.split('.')[0], // 확장자 제거
    fileName: file.filename,
    fileDescription: file.description,
    isActive: file.status === 'active',
    file_size: file.file_size,
    file_type: file.file_type,
    uploaded_at: file.uploaded_at,
    updated_at: file.updated_at
  });

  return (
    <MainLayout>
      <S.UploadPageContainer>
        {/* 페이지 헤더 */}
        <S.PageHeader>
          <S.HeaderTop>
            <div>
              <S.PageTitle>데이터 업로드</S.PageTitle>
              <S.PageSubtitle>
                데이터 파일을 업로드하고 관리하세요
              </S.PageSubtitle>
            </div>
            <S.HeaderActions>
              <S.RefreshButton onClick={handleRefresh} disabled={loading}>
                🔄 새로고침
              </S.RefreshButton>
              <S.FileCount>
                총 {pagination.total_files}개 데이터
              </S.FileCount>
            </S.HeaderActions>
          </S.HeaderTop>

          {/* 검색 및 필터 */}
          <S.FilterSection>
            {/* <S.SearchBox>
              <S.SearchInput
                type="text"
                placeholder="파일명으로 검색..."
                value={searchTerm}
                onChange={handleSearch}
                disabled={loading}
              />
              <S.SearchIcon>🔍</S.SearchIcon>
            </S.SearchBox> */}

            {/* <S.FilterButtons>
              <S.FilterButton 
                active={statusFilter === 'active'}
                onClick={() => handleFilterChange('active')}
                disabled={loading}
              >
                활성 파일
              </S.FilterButton>
            </S.FilterButtons> */}

            <S.SortButtons>
              <S.SortButton 
                active={sortBy === 'uploaded_at'}
                onClick={() => handleSortChange('uploaded_at')}
                disabled={loading}
              >
                업로드일 {sortBy === 'uploaded_at' && (sortOrder === 'desc' ? '↓' : '↑')}
              </S.SortButton>
              <S.SortButton 
                active={sortBy === 'filename'}
                onClick={() => handleSortChange('filename')}
                disabled={loading}
              >
                파일명 {sortBy === 'filename' && (sortOrder === 'desc' ? '↓' : '↑')}
              </S.SortButton>
              <S.SortButton 
                active={sortBy === 'file_size'}
                onClick={() => handleSortChange('file_size')}
                disabled={loading}
              >
                크기 {sortBy === 'file_size' && (sortOrder === 'desc' ? '↓' : '↑')}
              </S.SortButton>
            </S.SortButtons>
          </S.FilterSection>
        </S.PageHeader>

        {/* 메인 콘텐츠 영역 */}
        <S.ContentArea>
          {/* 전체 로딩 오버레이 */}
          {loading && (
            <S.LoadingOverlay>
              <S.LoadingSpinner />
              <S.LoadingText>파일 목록을 불러오는 중...</S.LoadingText>
            </S.LoadingOverlay>
          )}

          {/* 에러 표시 */}
          {error && (
            <S.ErrorMessage>
              <S.ErrorIcon>⚠️</S.ErrorIcon>
              <S.ErrorText>{error}</S.ErrorText>
              <S.RetryButton onClick={handleRefresh}>
                다시 시도
              </S.RetryButton>
            </S.ErrorMessage>
          )}

          <S.FileGridContainer>
            {/* 파일 카드들 */}
            {!loading && !error && files.map((file) => (
              <FileCard
                key={file.file_id}
                file={formatFileForDisplay(file)}
                onDataMapping={handleDataMapping}
                onMappingReset={handleMappingReset}
                onEditFile={handleEditFile}
                onFileUpdate={handleFileUpdate}
                onFileDelete={handleFileDelete}
              />
            ))}
            
            {/* 파일 추가 버튼 또는 입력 폼 */}
            {!loading && !error && (
              isAddingFile ? (
                <FileCardInput
                  onUpload={handleFileUpload}
                  onCancel={handleCancelUpload}
                />
              ) : (
                <FileAddButton onClick={handleAddFileClick} />
              )
            )}
          </S.FileGridContainer>

          {/* 빈 상태 표시 */}
          {!loading && !error && files.length === 0 && !isAddingFile && (
            <S.EmptyState>
              <S.EmptyIcon>📄</S.EmptyIcon>
              <S.EmptyTitle>
                {searchTerm ? '검색 결과가 없습니다' : '업로드된 파일이 없습니다'}
              </S.EmptyTitle>
              <S.EmptyDescription>
                {searchTerm 
                  ? `"${searchTerm}"와 일치하는 파일이 없습니다.` 
                  : '첫 번째 파일을 업로드하여 데이터 분석을 시작해보세요'
                }
              </S.EmptyDescription>
              {!searchTerm && (
                <S.EmptyButton onClick={handleAddFileClick}>
                  파일 업로드 시작하기
                </S.EmptyButton>
              )}
            </S.EmptyState>
          )}

          {/* 페이지네이션 */}
          {!loading && !error && files.length > 0 && pagination.total_pages > 1 && (
            <S.Pagination>
              <S.PaginationButton 
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_prev}
              >
                이전
              </S.PaginationButton>
              
              <S.PageNumbers>
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  const pageNum = Math.max(1, pagination.current_page - 2) + i;
                  if (pageNum > pagination.total_pages) return null;
                  
                  return (
                    <S.PageNumber
                      key={pageNum}
                      active={pageNum === pagination.current_page}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </S.PageNumber>
                  );
                })}
              </S.PageNumbers>
              
              <S.PaginationButton 
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next}
              >
                다음
              </S.PaginationButton>
              
              <S.PageInfo>
                {pagination.current_page} / {pagination.total_pages} 페이지
              </S.PageInfo>
            </S.Pagination>
          )}
        </S.ContentArea>

        {/* 도움말 섹션 */}
        {/* <S.HelpSection>
          <S.HelpTitle>💡 사용 가이드</S.HelpTitle>
          <S.HelpList>
            <S.HelpItem>
              <S.HelpIcon>📤</S.HelpIcon>
              <S.HelpText>
                <strong>파일 업로드:</strong> Excel, CSV, PDF, Word 파일을 업로드할 수 있습니다 (최대 100MB)
              </S.HelpText>
            </S.HelpItem>
            <S.HelpItem>
              <S.HelpIcon>🔍</S.HelpIcon>
              <S.HelpText>
                <strong>파일 검색:</strong> 파일명으로 검색하고 상태별로 필터링할 수 있습니다
              </S.HelpText>
            </S.HelpItem>
            <S.HelpItem>
              <S.HelpIcon>🗂️</S.HelpIcon>
              <S.HelpText>
                <strong>파일 관리:</strong> 파일 복사, 재업로드, 삭제 기능을 제공합니다
              </S.HelpText>
            </S.HelpItem>
            <S.HelpItem>
              <S.HelpIcon>📊</S.HelpIcon>
              <S.HelpText>
                <strong>정렬 기능:</strong> 업로드일, 파일명, 크기순으로 정렬할 수 있습니다
              </S.HelpText>
            </S.HelpItem>
          </S.HelpList>
        </S.HelpSection> */}
      </S.UploadPageContainer>
    </MainLayout>
  );
};

export default Upload;
